import React from 'react';
import { useChatContext } from '../providers/ChatProvider';

// Career-aware phase maps per conversation mode
// Each phase: { key, label } — displayed in the step ladder
const PHASE_MAP = {
  coach: [
    { key: 'READING_RESUME',   label: 'Reviewing Career Brain' },
    { key: 'ANALYZING',        label: 'Finding your weakest experience' },
    { key: 'GENERATING',       label: 'Preparing coaching question' },
    { key: 'UPDATING_RESUME',  label: 'Recording your answers' },
    { key: 'SCORING_ATS',      label: 'Scoring ATS impact' },
    { key: 'DONE',             label: 'Ready' },
  ],
  ask: [
    { key: 'READING_RESUME',   label: 'Reading career profile' },
    { key: 'ANALYZING',        label: 'Identifying what\'s missing' },
    { key: 'GENERATING',       label: 'Forming next question' },
    { key: 'UPDATING_RESUME',  label: 'Saving your answer' },
    { key: 'SCORING_ATS',      label: 'Updating career score' },
    { key: 'DONE',             label: 'Ready' },
  ],
  optimize: [
    { key: 'READING_RESUME',   label: 'Scanning ATS keywords' },
    { key: 'ANALYZING',        label: 'Finding gaps vs. target role' },
    { key: 'GENERATING',       label: 'Preparing optimization' },
    { key: 'UPDATING_RESUME',  label: 'Applying improvements' },
    { key: 'SCORING_ATS',      label: 'Recalculating ATS score' },
    { key: 'DONE',             label: 'Optimized' },
  ],
  verify: [
    { key: 'READING_RESUME',   label: 'Checking evidence vault' },
    { key: 'ANALYZING',        label: 'Reviewing uploaded documents' },
    { key: 'GENERATING',       label: 'Verifying credentials' },
    { key: 'UPDATING_RESUME',  label: 'Updating career profile' },
    { key: 'SCORING_ATS',      label: 'Scoring evidence quality' },
    { key: 'DONE',             label: 'Verified' },
  ],
  summarize: [
    { key: 'READING_RESUME',   label: 'Reading your full career' },
    { key: 'ANALYZING',        label: 'Analyzing achievements' },
    { key: 'GENERATING',       label: 'Drafting professional summary' },
    { key: 'UPDATING_RESUME',  label: 'Applying summary' },
    { key: 'SCORING_ATS',      label: 'Scoring ATS impact' },
    { key: 'DONE',             label: 'Summary ready' },
  ],
  rewrite: [
    { key: 'READING_RESUME',   label: 'Reading current content' },
    { key: 'ANALYZING',        label: 'Identifying improvements' },
    { key: 'GENERATING',       label: 'Rewriting for impact' },
    { key: 'UPDATING_RESUME',  label: 'Applying rewrites' },
    { key: 'SCORING_ATS',      label: 'Measuring ATS improvement' },
    { key: 'DONE',             label: 'Rewritten' },
  ],
  explain: [
    { key: 'READING_RESUME',   label: 'Reading your resume' },
    { key: 'ANALYZING',        label: 'Preparing explanation' },
    { key: 'GENERATING',       label: 'Writing response' },
    { key: 'DONE',             label: 'Ready' },
  ],
  import: [
    { key: 'READING_RESUME',   label: 'Preparing import' },
    { key: 'ANALYZING',        label: 'Parsing document structure' },
    { key: 'GENERATING',       label: 'Extracting career data' },
    { key: 'UPDATING_RESUME',  label: 'Filling career profile' },
    { key: 'SCORING_ATS',      label: 'Scoring imported resume' },
    { key: 'DONE',             label: 'Imported' },
  ],
};

// Default fallback phases
const DEFAULT_PHASES = [
  { key: 'READING_RESUME',   label: 'Reading resume' },
  { key: 'ANALYZING',        label: 'Analyzing context' },
  { key: 'GENERATING',       label: 'Generating response' },
  { key: 'UPDATING_RESUME',  label: 'Updating resume' },
  { key: 'SCORING_ATS',      label: 'Scoring ATS' },
  { key: 'DONE',             label: 'Done' },
];

function phaseIndex(phases, status) {
  return phases.findIndex((p) => p.key === status);
}

export default function TypingIndicator() {
  const {
    isStreaming,
    streamingText,
    chatStatus,
    theme,
    conversationMode,
    aiDebugEnabled,
    pipelineEvents
  } = useChatContext();

  if (!isStreaming && chatStatus === 'IDLE') return null;

  const phases = PHASE_MAP[conversationMode] || DEFAULT_PHASES;
  const currentIdx = phaseIndex(phases, chatStatus);
  const activeIdx = Math.max(0, currentIdx);
  const visiblePhases = phases.slice(0, activeIdx + 1);
  const T = theme === 'dark';
  const showPipelineEvents = aiDebugEnabled && !streamingText && isStreaming;

  return (
    <div className="flex flex-col gap-2.5 animate-message">
      {/* Phase ladder — only shown while waiting for reply stream to begin */}
      {!streamingText && (
        showPipelineEvents ? (
          <div
            style={{
              background: T ? 'rgba(15,15,20,0.85)' : 'rgba(248,250,252,0.95)',
              border: T ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.07)',
              borderRadius: 12,
              padding: '10px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 7,
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: T ? '#818cf8' : '#4f46e5', letterSpacing: '0.05em' }}>
              🧠 Pipeline Execution Events
            </div>
            {pipelineEvents.map((ev, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                <span>
                  {ev.status === 'success' ? '🟢' : ev.status === 'failed' ? '🔴' : '🟡'}
                </span>
                <span style={{ fontFamily: 'monospace', color: T ? '#e2e8f0' : '#0f172a' }}>
                  {ev.stage} {ev.duration_ms ? `(${ev.duration_ms}ms)` : ''}
                </span>
              </div>
            ))}
            {pipelineEvents.length === 0 && (
              <div style={{ color: '#64748b', fontSize: 11, fontStyle: 'italic' }}>Initializing pipeline stages...</div>
            )}
          </div>
        ) : (
          <div
            style={{
              background: T ? 'rgba(15,15,20,0.85)' : 'rgba(248,250,252,0.95)',
              border: T ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.07)',
              borderRadius: 12,
              padding: '10px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 7,
              position: 'relative',
            }}
          >
            {/* Mode badge */}
            {conversationMode && conversationMode !== 'ask' && (
              <div style={{
                position: 'absolute',
                top: -10,
                left: 10,
                background: T ? '#1e1b4b' : '#ede9fe',
                border: '1px solid rgba(124,58,237,0.35)',
                borderRadius: 20,
                padding: '2px 8px',
                fontSize: 9,
                fontWeight: 700,
                color: '#a78bfa',
                fontFamily: "'Inter', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}>
                {conversationMode}
              </div>
            )}

            {visiblePhases.map((phase, i) => {
              const isDone    = i < currentIdx;
              const isActive  = i === currentIdx || (currentIdx === -1 && i === 0);

              return (
                <div
                  key={phase.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    opacity: 1,
                    transition: 'opacity 0.3s',
                  }}
                >
                  {/* Step icon */}
                  {isDone ? (
                    <span style={{
                      width: 14, height: 14, borderRadius: '50%',
                      background: '#10b981',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 0 6px rgba(16,185,129,0.5)',
                    }}>
                      <svg width={8} height={8} fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                  ) : isActive ? (
                    <span style={{
                      width: 14, height: 14, borderRadius: '55%',
                      border: '2px solid #7c3aed',
                      borderTopColor: 'transparent',
                      animation: 'spin 0.7s linear infinite',
                      flexShrink: 0,
                      display: 'inline-block',
                    }} />
                  ) : (
                    <span style={{
                      width: 14, height: 14, borderRadius: '50%',
                      border: T ? '1.5px solid rgba(255,255,255,0.15)' : '1.5px solid rgba(0,0,0,0.15)',
                      flexShrink: 0,
                      display: 'inline-block',
                    }} />
                  )}

                  {/* Label */}
                  <span style={{
                    fontSize: 11,
                    fontWeight: isActive ? 600 : 400,
                    fontFamily: "'Inter', sans-serif",
                    color: isDone
                      ? '#10b981'
                      : isActive
                        ? T ? '#e9d5ff' : '#7c3aed'
                        : T ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.3)',
                    transition: 'color 0.3s',
                  }}>
                    {phase.label}
                    {isActive && chatStatus !== 'DONE' && (
                      <span style={{ marginLeft: 4, opacity: 0.7 }}>…</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Streaming text bubble */}
      {streamingText && (
        <div
          style={{
            background: T ? 'rgba(15,15,20,0.75)' : 'rgba(248,250,252,0.9)',
            border: T ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.06)',
            borderRadius: 12,
            padding: '8px 12px',
            fontSize: 11.5,
            lineHeight: 1.6,
            color: T ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.65)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {streamingText}
          <span style={{
            display: 'inline-block',
            width: 2,
            height: 13,
            background: '#7c3aed',
            marginLeft: 3,
            verticalAlign: 'middle',
            animation: 'pulse 1s ease-in-out infinite',
          }} />
        </div>
      )}
    </div>
  );
}
